import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';

import type TemporaryEmployeeTableVM from 'ab-viewModels/temporaryEmployee/table.viewModel';

import type { RootState } from 'af-reducers';

import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';
import LabelWithColor from 'af-components/LabelWithColor';
import Table from 'af-components/Table6';
import type { TabProps, Column, ButtonData, ItemBlueprint, BulkAction, LegacyTableRef } from 'af-components/Table6';
import UserCell from 'af-components/Table6/Cells/UserCell';
import type TableComponent from 'af-components/Table6/Table';

import * as TemporaryEmployeeActions from 'af-actions/temporaryEmployee';

type OwnProps = CustomRouteComponentProps;

type Props = OwnProps & ConnectedProps<typeof connector>;

const TemporaryEmployees: React.FC<Props> = (props: Props) => {
	const {
		companyName,
		history,
		location: { state: { orgAlias } },
		deactivateAccount,
		bulkDeactivateAccounts,
		reactivateAccount,
		fetchActive,
		fetchDeleted,
	} = props;

	const tableRef = React.useRef<TableComponent<TemporaryEmployeeTableVM>>(null);

	const renderAgency = React.useCallback(() => ({ original }) => (<LabelWithColor color={original.color} text={<strong>{original.agency}</strong>} />), []);

	const SHARED_COLUMNS: Column<TemporaryEmployeeTableVM>[] = React.useMemo(() => [
		{
			Header: 'Full Name',
			accessor: 'fullName',
			Cell: UserCell,
		},
		{
			Header: 'Agency',
			accessor: 'agency',
			Cell: renderAgency(),
		},
	], [renderAgency]);

	const COLUMNS_FOR_DELETED: Column<TemporaryEmployeeTableVM>[] = React.useMemo(() => [
		...SHARED_COLUMNS,
	], [SHARED_COLUMNS]);

	const COLUMNS_FOR_ACTIVE: Column<TemporaryEmployeeTableVM>[] = React.useMemo(() => [
		...SHARED_COLUMNS,
	], [SHARED_COLUMNS]);

	const DEACTIVATE_ACTION_LABEL = React.useMemo(() => (<><span className="icon-delete" />Deactivate</>), []);
	const DEACTIVATE_MODAL_CONFIRM_TEXT = 'Deactivate';
	const DEACTIVATE_MODAL_TITLE = 'Deactivate temporary laborers';

	const deleteAccountModalTitle = React.useCallback(() => 'Deactivating Temporary Labor', []);
	const deleteAccountModalBody = React.useCallback((original: TemporaryEmployeeTableVM) => `Are you sure you want to deactivate this User for company (${original.fullName})?`, []);
	const deleteAccountModalText = React.useCallback(() => 'Deactivate User', []);

	const reactivateAccountModalTitle = React.useCallback(() => 'Reactivating Temporary Labor', []);
	const reactivateAccountModalBody = React.useCallback((original: TemporaryEmployeeTableVM) => `You are about to reactivate the user (${original.fullName}) for company.`, []);
	const reactivateAccountModalText = React.useCallback(() => 'Reactivate', []);

	const onRowClick = React.useCallback(({ original }: { original: TemporaryEmployeeTableVM; }) => {
		history.push(CLIENT.COMPANY.RESOURCES.TEMPORARY_EMPLOYEE.PREVIEW(orgAlias, companyName, `${original.id}`));
	}, [companyName, history, orgAlias]);

	const editAccount = React.useCallback(async (original: TemporaryEmployeeTableVM) => {
		history.push(CLIENT.COMPANY.RESOURCES.TEMPORARY_EMPLOYEE.EDIT(orgAlias, companyName, `${original.id}`));
	}, [companyName, history, orgAlias]);

	const previewAccount = React.useCallback(async (original: TemporaryEmployeeTableVM) => {
		history.push(CLIENT.COMPANY.RESOURCES.TEMPORARY_EMPLOYEE.PREVIEW(orgAlias, companyName, `${original.id}`));
	}, [companyName, history, orgAlias]);

	const deactivate = React.useCallback(async (original: TemporaryEmployeeTableVM) => {

		await deactivateAccount(original.id);
	}, [deactivateAccount]);

	const bulkDeactivate = React.useCallback(async (ids: number[]) => {
		await bulkDeactivateAccounts(ids);
	}, [bulkDeactivateAccounts]);

	const refreshTable = React.useCallback(() => {
		if (tableRef?.current) {
			tableRef.current.refreshTable();
		}
	}, []);

	const reactivateUser = React.useCallback(async (original: TemporaryEmployeeTableVM) => {
		await reactivateAccount(original.id);
		refreshTable();
	}, [reactivateAccount, refreshTable]);

	const bulkActions = React.useCallback((): BulkAction[] => {
		return [
			{
				onActionConfirmation: bulkDeactivate,
				label: DEACTIVATE_ACTION_LABEL,
				key: 'deactivate',
				modalType: 'warning',
				modalSize: 'md',
				modalTitle: () => DEACTIVATE_MODAL_TITLE,
				confirmText: () => DEACTIVATE_MODAL_CONFIRM_TEXT,
				actionConfirmationBody: (ids: number[]) => `Are you sure you want to deactivate ${ids.length} temporary laborer${ids.length > 1 ? 's' : ''}`,
			},
		];
	}, [DEACTIVATE_ACTION_LABEL, bulkDeactivate]);

	const tabs = React.useCallback((): TabProps<TemporaryEmployeeTableVM>[] => {
		const activeUsersRowActions: ItemBlueprint<TemporaryEmployeeTableVM>[] = [
			{
				label: 'Edit',
				action: editAccount,
				shouldRefresh: false,
			},
			{
				label: 'Preview',
				action: previewAccount,
				shouldRefresh: false,
			},
			{
				label: 'Deactivate for Company',
				action: deactivate,
				hasModal: true,
				modalTitle: deleteAccountModalTitle,
				modalBody: deleteAccountModalBody,
				modalText: deleteAccountModalText,
				shouldRefresh: true,
			},
		];

		const deletedUsersRowActions: ItemBlueprint<TemporaryEmployeeTableVM>[] = [
			{
				label: 'Reactivate User',
				action: reactivateUser,
				hasModal: true,
				modalTitle: reactivateAccountModalTitle,
				modalBody: reactivateAccountModalBody,
				modalText: reactivateAccountModalText,
				shouldRefresh: true,
				hide: (original: TemporaryEmployeeTableVM) => !original.hasActiveAgency,
			},
		];

		const buttons: ButtonData[] = [
			{
				type: TableButtonType.PRIMARY,
				hasPermission: true,
				label: 'New Temporary Labor',
				onClick: async () => history.push(CLIENT.COMPANY.RESOURCES.TEMPORARY_EMPLOYEE.CREATE(orgAlias, companyName)),
			},
		];

		return [
			{
				label: 'All',
				columns: COLUMNS_FOR_ACTIVE,
				selectable: true,
				hasSearchInput: true,
				searchLabel: 'Users',
				buttons,
				fetch: fetchActive,
				additionalBulkActions: bulkActions(),
				onRowClick: onRowClick,
				rowActions: activeUsersRowActions,
			},
			{
				label: 'Recently Deactivated',
				columns: COLUMNS_FOR_DELETED,
				selectable: false,
				hasSearchInput: true,
				searchLabel: 'Users',
				buttons,
				fetch: fetchDeleted,
				rowActions: deletedUsersRowActions,
			},
		];
	}, [COLUMNS_FOR_ACTIVE,
		COLUMNS_FOR_DELETED,
		bulkActions,
		companyName,
		deactivate,
		deleteAccountModalBody,
		deleteAccountModalText,
		deleteAccountModalTitle,
		editAccount,
		fetchActive,
		fetchDeleted,
		history,
		onRowClick,
		orgAlias,
		previewAccount,
		reactivateAccountModalBody,
		reactivateAccountModalText,
		reactivateAccountModalTitle,
		reactivateUser,
	]);

	return (
		<>
			<Breadcrumbs items={[{ label: 'Temporary labor' }]} />
			<Table
				ref={tableRef as unknown as LegacyTableRef<TemporaryEmployeeTableVM>}
				tableName={TableNameEnum.TEMPORARY_EMPLOYEE}
				tabs={tabs()}
			/>
		</>
	);
};

const mapStateToProps = (state: RootState) => {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
};

function mapDispatchToProps() {
	return {
		fetchActive: TemporaryEmployeeActions.findAllForCompanyTable,
		fetchDeleted: TemporaryEmployeeActions.findAllDeletedForCompanyTable,
		deactivateAccount: TemporaryEmployeeActions.remove,
		bulkDeactivateAccounts: TemporaryEmployeeActions.bulkRemove,
		reactivateAccount: TemporaryEmployeeActions.reactivate,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(TemporaryEmployees);
